import { CpuIcon, RotateCcwIcon, UserIcon } from "lucide-react";

import { Column } from "@/components/Column";
import { Text } from "@/components/Text";
import {
  FIELD_CONTENT_CLASS,
  FIELD_TRIGGER_CLASS,
  SadieSimulatorField,
} from "@/components/tools/sadie-simulator/SadieSimulatorField";
import { SadieSimulatorSessions } from "@/components/tools/sadie-simulator/SadieSimulatorSessions";
import { SadieSimulatorStatus } from "@/components/tools/sadie-simulator/SadieSimulatorStatus";
import { Button } from "@ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@ui/select";

import {
  SadieSimulatorSession,
  SadieSimulatorState,
  SadieSimulatorTester,
} from "@/types";

// The select's value for the usual model, since an item can't take null.
const PRODUCTION_MODEL = "production";

export const SadieSimulatorControls = ({
  isStarting,
  isStopping,
  model,
  models,
  onModelChange,
  onStart,
  onStop,
  onTesterChange,
  selectedTalentId,
  sessions,
  simulator,
  testers,
}: {
  isStarting: boolean;
  isStopping: boolean;
  model: string | null;
  models: string[];
  onModelChange: (model: string | null) => void;
  onStart: () => void;
  onStop: () => void;
  onTesterChange: (talentId: number) => void;
  selectedTalentId: number;
  sessions: SadieSimulatorSession[];
  simulator: SadieSimulatorState | null;
  testers: SadieSimulatorTester[];
}) => {
  /* Helpers */
  const testerLabel = (tester: SadieSimulatorTester) =>
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    `${tester.firstName || "Tester"} (${String(tester.talentId)})`;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const modelValue = model || PRODUCTION_MODEL;

  /* Render */
  return (
    <Column className="gap-5">
      <SadieSimulatorField
        icon={<UserIcon />}
        id="sadie-sim-tester"
        label="Tester"
      >
        <Select
          disabled={isStarting}
          onValueChange={(value) => {
            onTesterChange(Number(value));
          }}
          value={String(selectedTalentId)}
        >
          <SelectTrigger className={FIELD_TRIGGER_CLASS} id="sadie-sim-tester">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={FIELD_CONTENT_CLASS}>
            {testers.map((tester) => (
              <SelectItem key={tester.talentId} value={String(tester.talentId)}>
                {testerLabel(tester)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </SadieSimulatorField>

      <SadieSimulatorField
        icon={<CpuIcon />}
        id="sadie-sim-model"
        label="Model"
      >
        <Select
          disabled={isStarting}
          onValueChange={(value) => {
            onModelChange(value === PRODUCTION_MODEL ? null : value);
          }}
          value={modelValue}
        >
          <SelectTrigger className={FIELD_TRIGGER_CLASS} id="sadie-sim-model">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className={FIELD_CONTENT_CLASS}>
            <SelectItem value={PRODUCTION_MODEL}>Production model</SelectItem>
            {models.map((m) => (
              <SelectItem key={m} value={m}>
                {m}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Text className="text-xs font-normal text-slate-500">
          Takes effect when you start over.
        </Text>
      </SadieSimulatorField>

      <Button disabled={isStarting} onClick={onStart}>
        <RotateCcwIcon className="h-4 w-4" />
        {isStarting ? "Starting over" : "Start over"}
      </Button>

      <SadieSimulatorStatus
        isStopping={isStopping}
        onStop={onStop}
        simulator={simulator}
      />

      {sessions.length > 0 ? (
        <SadieSimulatorSessions sessions={sessions} />
      ) : null}
    </Column>
  );
};
